import type { Auth, User } from "firebase/auth";
import { signOut } from "./signOut";
import { type FormEvent, useEffect, useState } from "react";
import * as api from "./api";
import { age, attention, battery, level, lowBattery, readingTime } from "./format";
import { useClock, useSection } from "./hooks";
import { cleanLabel, labelError, labelFromText, type Undo, undoFor } from "./label";
import { useHash } from "./Menu";
import { SERVER_PROBLEM } from "./save";
import { TankDetails } from "./TankDetail";
import { Hint } from "./Hint";
import { Skeleton } from "./Skeleton";

type Status =
  | { ok: true; text: string; undo?: Undo }
  | { ok: false; text: string; field?: boolean; session?: boolean };

function failure(e: unknown): Status {
  const status = e instanceof api.ApiError ? e.status : 0;
  if (status === 401) return { ok: false, session: true, text: "Your session ended." };
  if ((status === 403 || status === 422) && e instanceof api.ApiError && e.detail) {
    return { ok: false, field: true, text: e.detail };
  }
  if (status >= 500) return { ok: false, text: SERVER_PROBLEM };
  return { ok: false, text: "The name was not saved. Check your connection, then try again." };
}

export function TestChip() {
  return (
    <Hint text="A test container. It is not in the field." stop={false}>
      <span className="chip-test" data-testid="tank-test">
        Test
      </span>
    </Hint>
  );
}

/** The facts, the use switch, the name form and the readings of one sensor tank. */
export function TankPanel({
  tank,
  onTank,
  auth,
  user,
  timeZone,
  now,
  heading = 2,
}: {
  tank: api.Tank;
  onTank: (next: api.Tank) => void;
  auth: Auth;
  user: User;
  timeZone: string;
  now: Date;
  heading?: 2 | 4;
}) {
  const [name, setName] = useState(tank.label);
  const [status, setStatus] = useState<Status | null>(null);
  const [saving, setSaving] = useState(false);
  const id = tank.id;

  const change = async (action: Undo, done: string, offerUndo: boolean) => {
    const before = undoFor(tank);
    setSaving(true);
    setStatus(null);
    try {
      const token = await user.getIdToken();
      const next =
        action.kind === "rename" ? await api.renameTank(token, id, action.label) : await api.resetTankLabel(token, id);
      onTank(next);
      setName(next.label);
      const text = action.kind === "reset" && next.label_from === "default" ? "The container has its default name again." : done;
      setStatus({ ok: true, text, undo: offerUndo ? before : undefined });
    } catch (e) {
      setStatus(failure(e));
    } finally {
      setSaving(false);
    }
  };

  const setUse = async (inField: boolean) => {
    setSaving(true);
    setStatus(null);
    try {
      onTank(await api.setTankUse(await user.getIdToken(), id, inField));
      setStatus({ ok: true, text: inField ? "The container is in the field." : 'The container is marked "Test".' });
    } catch (e) {
      const found = failure(e);
      const detail = e instanceof api.ApiError && e.detail ? e.detail : "The mark was not saved. Check your connection, then try again.";
      setStatus(!found.ok && (found.session || found.text === SERVER_PROBLEM) ? found : { ok: false, text: detail });
    } finally {
      setSaving(false);
    }
  };

  const save = (event: FormEvent) => {
    event.preventDefault();
    const error = labelError(name);
    if (error) setStatus({ ok: false, field: true, text: error });
    else void change({ kind: "rename", label: cleanLabel(name) }, "Saved.", true);
  };

  const state = tank.state;
  const stale = state && attention(tank, now) === "stale";
  const fieldError = status && !status.ok && status.field ? status.text : null;
  const undo = status?.ok ? status.undo : undefined;
  const field = `tank-name-${id}`;
  return (
    <>
      {tank.can_set_use && (
        <p className="tank-use">
          <label>
            <input
              type="checkbox"
              role="switch"
              data-testid="tank-in-field"
              checked={tank.in_field}
              disabled={saving}
              onChange={(e) => void setUse(e.target.checked)}
              aria-describedby={`tank-use-hint-${id}`}
            />{" "}
            Container in the field
          </label>
          <span id={`tank-use-hint-${id}`} className="hint">
            A test container gives no fill estimate and only administrators see it.
          </span>
        </p>
      )}
      <dl className="facts">
        <dt>Level</dt>
        <dd data-testid="tank-level">
          <span className={`level level-${state?.measurement.level_state ?? "none"}`}>{level(tank)}</span>
        </dd>
        <dt>Battery</dt>
        <dd>
          {battery(tank)}
          {lowBattery(tank) && <span className="chip-low">⚠ Low</span>}
        </dd>
        <dt>Last reading</dt>
        <dd data-testid="tank-age">
          {state ? (
            <>
              {age(state.observed_at, now)} · {readingTime(state.observed_at, timeZone)}
              {stale && (
                <span className="chip-stale" data-testid="tank-stale">
                  ⚠ Stale
                </span>
              )}
            </>
          ) : (
            <span className="chip-stale" data-testid="tank-stale">
              ⚠ Never reported
            </span>
          )}
        </dd>
        <dt>{tank.devices.length > 1 ? "Devices" : "Device"}</dt>
        <dd data-testid="tank-devices">{tank.devices.length ? tank.devices.join(", ") : "None"}</dd>
      </dl>
      <p className="footnote">Battery values are estimates from the sensor and can be wrong.</p>
      {tank.can_edit ? (
        <form onSubmit={save} noValidate>
          <label htmlFor={field}>Container name</label>
          <input
            id={field}
            data-testid="tank-name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setStatus(null);
            }}
            aria-describedby={fieldError ? `${field}-error ${field}-hint` : `${field}-hint`}
            aria-invalid={fieldError ? true : undefined}
          />
          {fieldError && (
            <p id={`${field}-error`} role="alert" className="error" data-testid="rename-status">
              {fieldError}
            </p>
          )}
          <p id={`${field}-hint`} className="hint">
            {labelFromText(tank)}
          </p>
          <div className="actions">
            <button className="primary" data-testid="save-name" disabled={saving || cleanLabel(name) === tank.label}>
              {saving ? "Saving…" : "Save name"}
            </button>
            {tank.label_from === "user" && (
              <button
                type="button"
                data-testid="use-device-name"
                disabled={saving}
                onClick={() => void change({ kind: "reset" }, "The container has its device name again.", true)}
              >
                Use the device name
              </button>
            )}
          </div>
        </form>
      ) : (
        <p className="hint">{labelFromText(tank)}</p>
      )}
      {status && !fieldError && (
        <p role={status.ok ? "status" : "alert"} className={status.ok ? undefined : "error"} data-testid="rename-status">
          {status.text}
          {undo && (
            <>
              {" "}
              <button
                type="button"
                className="link"
                data-testid="undo-rename"
                disabled={saving}
                onClick={() => void change(undo, "The name is back.", false)}
              >
                Undo
              </button>
            </>
          )}
          {!status.ok && status.session && (
            <>
              {" "}
              <button onClick={() => signOut(auth)}>Sign in again</button>
            </>
          )}
        </p>
      )}
      <TankDetails id={tank.id} user={user} timeZone={timeZone} now={now} heading={heading} />
    </>
  );
}

type Found = api.Tank[] | "failed" | undefined;

/** The Sensor section of an account page: one panel for each sensor tank of its containers. */
export function SensorSection({
  tankIds,
  auth,
  user,
  timeZone,
}: {
  tankIds: string[];
  auth: Auth;
  user: User;
  timeZone: string;
}) {
  const now = useClock();
  const [tanks, setTanks] = useState<Found>(undefined);
  const [attempt, setAttempt] = useState(0);
  const hash = useHash();
  const key = tankIds.join(",");
  useEffect(() => {
    let live = true;
    setTanks(undefined);
    user
      .getIdToken()
      .then((token) =>
        Promise.all(
          key.split(",").map((id) =>
            api.tank(token, id).catch((e) => {
              if (e instanceof api.ApiError && e.status === 404) return null;
              throw e;
            }),
          ),
        ),
      )
      .then((found) => live && setTanks(found.filter((tank): tank is api.Tank => tank !== null)))
      .catch(() => live && setTanks("failed"));
    return () => {
      live = false;
    };
  }, [key, user, attempt]);
  useSection(hash === "#sensor" ? hash : "", tanks === undefined ? "loading" : "shown");
  if (Array.isArray(tanks) && tanks.length === 0) return null;
  const replace = (next: api.Tank) => setTanks((all) => (Array.isArray(all) ? all.map((tank) => (tank.id === next.id ? next : tank)) : all));
  return (
    <section id="sensor" tabIndex={-1} aria-labelledby="account-sensor" data-testid="account-sensor">
      <h2 id="account-sensor">{Array.isArray(tanks) && tanks.length > 1 ? "Sensors" : "Sensor"}</h2>
      {tanks === undefined ? (
        <Skeleton rows={2} />
      ) : tanks === "failed" ? (
        <p role="alert" className="error">
          Cannot load the sensor. Check your connection.{" "}
          <button onClick={() => setAttempt((n) => n + 1)}>Try again</button>
        </p>
      ) : (
        tanks.map((tank) => (
          <div key={tank.id} className="sensor-tank" data-testid="sensor-tank">
            <h3 data-testid="sensor-label">
              {tank.label}
              {!tank.in_field && <TestChip />}
            </h3>
            <TankPanel tank={tank} onTank={replace} auth={auth} user={user} timeZone={timeZone} now={now} heading={4} />
          </div>
        ))
      )}
    </section>
  );
}
